import React, { useEffect, useState, useRef } from 'react';
import { UnifiedMessage, WhatsAppLead } from '../../types/whatsapp';
import { MessageItem } from './MessageItem';
import { SendIcon, PaperclipIcon, SmileIcon, PhoneIcon, MoreVerticalIcon, MessageCircleIcon, MessageSquareIcon, ChevronDownIcon, MapPinIcon } from 'lucide-react';
import { useViewMode } from '../../context/ViewModeContext';
interface UnifiedThreadProps {
  lead: WhatsAppLead;
  messages: UnifiedMessage[];
  onSendMessage: (content: string, channel: 'whatsapp' | 'sms') => void;
}
// Map dealer names to location codes
const DEALER_LOCATION_MAP: Record<string, {
  code: string;
  name: string;
}> = {
  'Mumbai Motors': {
    code: 'MH401',
    name: 'Mumbai Motors'
  },
  'Delhi Auto Hub': {
    code: 'DL302',
    name: 'Delhi Auto Hub'
  },
  'Pune Motors': {
    code: 'MH201',
    name: 'Pune Motors'
  },
  'Koncept Auto': {
    code: 'HR407',
    name: 'Koncept Auto'
  },
  'Chennai Cars': {
    code: 'TN101',
    name: 'Chennai Cars'
  }
};
export function UnifiedThread({
  lead,
  messages,
  onSendMessage
}: UnifiedThreadProps) {
  const {
    viewMode
  } = useViewMode();
  const [inputMessage, setInputMessage] = useState('');
  const [activeChannel, setActiveChannel] = useState<'whatsapp' | 'sms'>('whatsapp');
  const [showChannelDropdown, setShowChannelDropdown] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const locationInfo = lead.dealer ? DEALER_LOCATION_MAP[lead.dealer] : undefined;
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowChannelDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSend = () => {
    if (!inputMessage.trim()) return;
    onSendMessage(inputMessage, activeChannel);
    setInputMessage('');
  };
  return <div className="flex flex-col h-full bg-[#f0f2f5]" data-id="element-948">
      {/* Header */}
      <div className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm z-10" data-id="element-949">
        <div className="flex items-center space-x-3" data-id="element-950">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg" data-id="element-951">
            {lead.name.charAt(0)}
          </div>
          <div data-id="element-952">
            <h3 className="font-semibold text-gray-900" data-id="element-953">{lead.name}</h3>
            <div className="flex items-center text-xs text-gray-500 space-x-2" data-id="element-954">
              <span data-id="element-955">{lead.phone}</span>
              <span data-id="element-956">•</span>
              <span className="text-green-600 font-medium" data-id="element-957">Online</span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3" data-id="element-958">
          {/* Location Badge - Only show for Brand view */}
          {viewMode === 'brand' && locationInfo && <div className="flex items-center px-3 py-1.5 bg-slate-100 rounded-lg" data-id="element-959">
              <MapPinIcon size={14} className="text-slate-500 mr-1.5" data-id="element-960" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-961">
                {locationInfo.code}
              </span>
              <span className="text-xs text-slate-400 ml-1.5" data-id="element-962">
                • {locationInfo.name}
              </span>
            </div>}
          <button className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors" title="Call" data-id="element-963">
            <PhoneIcon size={20} data-id="element-964" />
          </button>
          <button className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors" data-id="element-965">
            <MoreVerticalIcon size={20} data-id="element-966" />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-6 bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat" data-id="element-967">
        {messages.map(msg => <MessageItem key={msg.id} message={msg} locationCode={viewMode === 'brand' ? locationInfo?.code : undefined} locationName={viewMode === 'brand' ? locationInfo?.name : undefined} data-id="element-968" />)}
        <div ref={messagesEndRef} data-id="element-969" />
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-gray-200 p-4" data-id="element-970">
        <div className="flex items-end space-x-2 bg-gray-50 p-2 rounded-xl border border-gray-200" data-id="element-971">
          {/* Channel Selector Dropdown */}
          <div className="relative" ref={dropdownRef} data-id="element-972">
            <button onClick={() => setShowChannelDropdown(!showChannelDropdown)} className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all ${activeChannel === 'whatsapp' ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100' : 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'}`} data-id="element-973">
              {activeChannel === 'whatsapp' ? <MessageCircleIcon size={18} data-id="element-974" /> : <MessageSquareIcon size={18} data-id="element-975" />}
              <ChevronDownIcon size={16} data-id="element-976" />
            </button>

            {/* Dropdown Menu */}
            {showChannelDropdown && <div className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10" data-id="element-977">
                <button onClick={() => {
              setActiveChannel('whatsapp');
              setShowChannelDropdown(false);
            }} className={`w-full flex items-center space-x-3 px-4 py-2.5 text-sm transition-colors ${activeChannel === 'whatsapp' ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-978">
                  <MessageCircleIcon size={18} className="text-green-600" data-id="element-979" />
                  <span className="font-medium" data-id="element-980">WhatsApp</span>
                </button>
                <button onClick={() => {
              setActiveChannel('sms');
              setShowChannelDropdown(false);
            }} className={`w-full flex items-center space-x-3 px-4 py-2.5 text-sm transition-colors ${activeChannel === 'sms' ? 'bg-purple-50 text-purple-700' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-981">
                  <MessageSquareIcon size={18} className="text-purple-600" data-id="element-982" />
                  <span className="font-medium" data-id="element-983">SMS</span>
                </button>
              </div>}
          </div>

          <button className="p-2 text-gray-500 hover:bg-gray-200 rounded-full transition-colors" data-id="element-984">
            <SmileIcon size={20} data-id="element-985" />
          </button>
          <button className="p-2 text-gray-500 hover:bg-gray-200 rounded-full transition-colors" data-id="element-986">
            <PaperclipIcon size={20} data-id="element-987" />
          </button>
          <textarea value={inputMessage} onChange={e => setInputMessage(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }} placeholder="Type a message..." className="flex-1 bg-transparent border-0 focus:ring-0 resize-none max-h-32 py-2 text-gray-900 placeholder-gray-400" rows={1} data-id="element-988" />
          <button onClick={handleSend} disabled={!inputMessage.trim()} className={`p-2 rounded-full transition-all transform hover:scale-105 ${inputMessage.trim() ? activeChannel === 'whatsapp' ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-gray-200 text-gray-400'}`} data-id="element-989">
            <SendIcon size={20} data-id="element-990" />
          </button>
        </div>
        <div className="flex justify-between items-center mt-2 px-1" data-id="element-991">
          <span className="text-xs text-gray-400" data-id="element-992">
            Press Enter to send, Shift+Enter for new line
          </span>
          <button className="text-xs text-blue-600 font-medium hover:underline" data-id="element-993">
            Use Template
          </button>
        </div>
      </div>
    </div>;
}